/* eslint-disable max-statements */

import { Box, Button, CloseButton, Group, LoadingOverlay, Stack, Text, Title } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';

import {
  Batch,
  DeclarationCustomsFlowType,
  DeclarationCustomsType,
  DeclarationStatus,
  EnvironmentCountries,
  getTotal,
  PostFlowDeclarationTypes,
  TemplateType,
} from '@e-origin/shared';

import { useBatch, useSendAllDeclarationsToIdms, useSendAllDeclarationsToPlda, useSendBatchToBeGate } from '@hooks/batch';
import { useDeclarant } from '@hooks/declarant';
import { useGetDeclarationTypeForBatch, useSendOneHv } from '@hooks/declaration';
import { queryClient } from '@hooks/queryClient';
import { useSettings } from '@hooks/useSettings';
import { classes } from '@styles';

interface SendBatchProps {
  batchId: string;
  title: string;
  lrn?: string;
  onHide: () => void;
}

enum SendBatchOptions {
  BE_GATE = 'BE_GATE',
  PLDA = 'PLDA',
  PRE_IDMS = 'PRE_IDMS',
  POST_IDMS = 'POST_IDMS',
  PRE_DECO = 'PRE_DECO',
  POST_DECO = 'POST_DECO',
}

const DECLARATION_GROUPS_QUERY_KEY = 'declaration-groups';

const countDeclarationsForPreFlow = (batch: Batch, declarationType: string): number => {
  if (declarationType in PostFlowDeclarationTypes) {
    return 0;
  }

  return (
    getTotal(batch, DeclarationStatus.NOT_SENT, DeclarationCustomsFlowType.PRE) +
    getTotal(batch, DeclarationStatus.ANALYSIS, DeclarationCustomsFlowType.PRE) +
    getTotal(batch, DeclarationStatus.REJECTED, DeclarationCustomsFlowType.PRE)
  );
};

const countDeclarationsForPostFlow = (batch: Batch, declarationType: string): number => {
  if (declarationType in PostFlowDeclarationTypes) {
    return (
      getTotal(batch, DeclarationStatus.NOT_SENT, DeclarationCustomsFlowType.POST) +
      getTotal(batch, DeclarationStatus.ANALYSIS, DeclarationCustomsFlowType.POST) +
      getTotal(batch, DeclarationStatus.REJECTED, DeclarationCustomsFlowType.POST)
    );
  }

  return (
    getTotal(batch, DeclarationStatus.ACCEPTED, DeclarationCustomsFlowType.PRE) +
    getTotal(batch, DeclarationStatus.IN_CONTROL, DeclarationCustomsFlowType.PRE) +
    getTotal(batch, DeclarationStatus.REJECTED, DeclarationCustomsFlowType.POST) -
    (batch?.statistics?.invalidationPending || 0)
  );
};

const lowValuesBEModalDescription = (batch: Batch) => {
  let declarationsToSendCount = batch.statistics.rejected;

  if (batch.statistics?.sent < batch.statistics?.total) {
    declarationsToSendCount = batch.statistics.total - batch.statistics.sent + batch.statistics.rejected;
  }

  return `Are you sure you want to send ${declarationsToSendCount} declarations for this batch?`;
};

const buildLowValuesModalDescription = (batch: Batch, declarationType: any, country: EnvironmentCountries): string => {
  if (country === EnvironmentCountries.BE) {
    return lowValuesBEModalDescription(batch);
  }

  return `Choose action: send PRE (${countDeclarationsForPreFlow(
    batch,
    declarationType,
  )}) or send POST (${countDeclarationsForPostFlow(batch, declarationType)})`;
};

export const SendBatch: React.FC<SendBatchProps> = ({ batchId, lrn, title, onHide }) => {
  const { data: batch, isLoading } = useBatch(batchId);
  const [showIDMSSendButtons, setShowIDMSSendButtons] = useState(batch?.isExport);

  const sendAllDeclarationsToIDMSMutation = useSendAllDeclarationsToIdms();
  const sendAllDeclarationsToPLDAMutation = useSendAllDeclarationsToPlda();
  const sendBatchToBeGateMutation = useSendBatchToBeGate();
  const useSendOneHvMutation = useSendOneHv();

  const { data: declarant } = useDeclarant();

  const {
    data: { declarationType, sentTo },
  } = useGetDeclarationTypeForBatch({ batchId });

  const { data: settings } = useSettings();

  const showPre = useMemo(() => countDeclarationsForPreFlow(batch, declarationType) > 0, [declarationType, batch]);
  const showPost = useMemo(() => countDeclarationsForPostFlow(batch, declarationType) > 0, [declarationType, batch]);

  useEffect(() => {
    if (batch?.isExport || sentTo === DeclarationCustomsType.IDMS) {
      setShowIDMSSendButtons(true);
    }
  }, [batch, sentTo]);

  const handleBatchSending = async (option: SendBatchOptions) => {
    onHide();

    switch (option) {
      case SendBatchOptions.BE_GATE: {
        if (!declarant?.virtuals?.isPldaDisabled) {
          await sendBatchToBeGateMutation.mutateAsync({ batchId: batch._id });
        }
        break;
      }
      case SendBatchOptions.PLDA: {
        if (!declarant?.virtuals?.isPldaDisabled) {
          await sendAllDeclarationsToPLDAMutation.mutateAsync({ batchId: batch._id });
        }
        break;
      }
      case SendBatchOptions.PRE_IDMS: {
        if (lrn) {
          await useSendOneHvMutation.mutateAsync({ lrn, flow: DeclarationCustomsFlowType.PRE });
          queryClient.invalidateQueries({ queryKey: [DECLARATION_GROUPS_QUERY_KEY] });
          break;
        }

        await sendAllDeclarationsToIDMSMutation.mutateAsync({ batchId: batch._id, flow: DeclarationCustomsFlowType.PRE });
        break;
      }
      case SendBatchOptions.POST_IDMS: {
        if (lrn) {
          await useSendOneHvMutation.mutateAsync({ lrn, flow: DeclarationCustomsFlowType.POST });
          queryClient.invalidateQueries({ queryKey: [DECLARATION_GROUPS_QUERY_KEY] });
          break;
        }

        await sendAllDeclarationsToIDMSMutation.mutateAsync({ batchId: batch._id, flow: DeclarationCustomsFlowType.POST });
        break;
      }
      case SendBatchOptions.PRE_DECO: {
        if (lrn) {
          await useSendOneHvMutation.mutateAsync({ lrn, flow: DeclarationCustomsFlowType.PRE });
          queryClient.invalidateQueries({ queryKey: [DECLARATION_GROUPS_QUERY_KEY] });
          break;
        }

        await sendAllDeclarationsToIDMSMutation.mutateAsync({ batchId: batch._id, flow: DeclarationCustomsFlowType.PRE });
        break;
      }
      case SendBatchOptions.POST_DECO: {
        if (lrn) {
          await useSendOneHvMutation.mutateAsync({ lrn, flow: DeclarationCustomsFlowType.POST });
          queryClient.invalidateQueries({ queryKey: [DECLARATION_GROUPS_QUERY_KEY] });
          break;
        }

        await sendAllDeclarationsToIDMSMutation.mutateAsync({ batchId: batch._id, flow: DeclarationCustomsFlowType.POST });
        break;
      }
      default:
        break;
    }

    queryClient.invalidateQueries({ queryKey: ['batches'] });
  };

  if (!batch || !declarationType) return <></>;

  if (!batch) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <>
      <Group bg={'rgb(246, 249, 255)'} style={{ borderBottom: '1px solid rgb(222, 222, 222)' }} justify="space-between" align="center" p="lg">
        <Stack gap="xs">
          <Title order={4}>{title}</Title>
          <Text>{batch.name}</Text>
        </Stack>
        <CloseButton onClick={onHide} />
      </Group>

      {batch.templateType === TemplateType.HIGH_VALUE_H1 && settings?.country === EnvironmentCountries.BE && !showIDMSSendButtons && (
        <Box p={15}>
          <Group justify="center" gap={15}>
            <Button
              type="button"
              onClick={() => handleBatchSending(SendBatchOptions.BE_GATE)}
              disabled={batch.statistics.beGateSent > 0 || !!declarant?.virtuals?.isPldaDisabled}
              variant="filled"
              size="lg"
              loading={sendBatchToBeGateMutation.isPending}
            >
              BE-GATE
            </Button>

            <Button
              data-pw="plda-btn"
              type="button"
              onClick={() => handleBatchSending(SendBatchOptions.PLDA)}
              variant="filled"
              size="lg"
              loading={sendAllDeclarationsToPLDAMutation.isPending}
              disabled={!!declarant?.virtuals?.isPldaDisabled}
            >
              PLDA
            </Button>

            {settings?.highValuesIDMSEnabled && !showIDMSSendButtons && (
              <Button data-pw="idms-btn" type="button" onClick={() => setShowIDMSSendButtons(true)} variant="filled" size="lg">
                IDMS
              </Button>
            )}
          </Group>
        </Box>
      )}
      {batch.templateType !== TemplateType.LOW_VALUE_H7 && showIDMSSendButtons && settings.country === EnvironmentCountries.BE && (
        <Box p={15}>
          <Group justify="center" gap={15}>
            <Button
              data-pw="send-pre-btn"
              type="button"
              onClick={() => handleBatchSending(SendBatchOptions.PRE_IDMS)}
              disabled={!showPre}
              variant="filled"
              size="lg"
              loading={showPre && (useSendOneHvMutation.isPending || sendAllDeclarationsToIDMSMutation.isPending)}
            >
              Send PRE
            </Button>
            <Button
              data-pw="send-post-btn"
              type="button"
              onClick={() => handleBatchSending(SendBatchOptions.POST_IDMS)}
              disabled={!showPost}
              variant="filled"
              size="lg"
              loading={showPost && (useSendOneHvMutation.isPending || sendAllDeclarationsToIDMSMutation.isPending)}
            >
              Send POST
            </Button>
          </Group>
        </Box>
      )}

      {batch.templateType === TemplateType.HIGH_VALUE_H1 && settings?.country === EnvironmentCountries.NL && (
        <Box p={15}>
          <Group justify="center" gap={15}>
            <Button
              type="button"
              onClick={() => handleBatchSending(SendBatchOptions.PRE_DECO)}
              disabled={!showPre}
              variant="filled"
              size="lg"
              loading={showPre && (useSendOneHvMutation.isPending || sendAllDeclarationsToIDMSMutation.isPending)}
            >
              Send PRE
            </Button>
            <Button
              type="button"
              onClick={() => handleBatchSending(SendBatchOptions.POST_DECO)}
              disabled={!showPost}
              variant="filled"
              size="lg"
              loading={showPost && (useSendOneHvMutation.isPending || sendAllDeclarationsToIDMSMutation.isPending)}
            >
              Send POST
            </Button>
          </Group>
        </Box>
      )}

      {batch.templateType !== TemplateType.HIGH_VALUE_H1 && (
        <>
          <Box py={30} px={45}>
            <Text mb={20}>{buildLowValuesModalDescription(batch, declarationType, settings?.country)}</Text>
          </Box>
          <Box className={classes.footer}>
            <Button type="button" onClick={onHide} variant="outline" size="lg">
              Cancel
            </Button>
            {settings?.country === EnvironmentCountries.BE && (
              <Button
                data-pw="send-btn"
                type="button"
                onClick={() => handleBatchSending(SendBatchOptions.PRE_IDMS)}
                variant="filled"
                size="lg"
                loading={useSendOneHvMutation.isPending || sendAllDeclarationsToIDMSMutation.isPending}
              >
                Send
              </Button>
            )}
            {settings?.country === EnvironmentCountries.NL && (
              <>
                <Button
                  type="button"
                  onClick={() => handleBatchSending(SendBatchOptions.PRE_DECO)}
                  disabled={!showPre}
                  variant="filled"
                  size="lg"
                  loading={sendAllDeclarationsToIDMSMutation.isPending}
                >
                  Send PRE
                </Button>
                <Button
                  type="button"
                  onClick={() => handleBatchSending(SendBatchOptions.POST_DECO)}
                  disabled={!showPost}
                  variant="filled"
                  size="lg"
                  loading={sendAllDeclarationsToIDMSMutation.isPending}
                >
                  Send POST
                </Button>
              </>
            )}
          </Box>
        </>
      )}
    </>
  );
};
